// ABOUTME: Day drill-down panel for FamilyInsightsPage — shown when a Chart 1 data point is clicked
// ABOUTME: Displays heart day status, behavior counts, hourly timeline (single child), and family summary (all family)

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Favorite as HeartIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { Child } from '../types/api';
import { insightsAPI } from '../services/api';
import AnalyticsTimeChart from './AnalyticsTimeChart';
import { computePositivityRatio } from '../utils/insightsUtils';

interface BehaviorBreakdown {
  stars: number;
  triangles: number;
  squares: number;
}

interface InsightsDrillDownPanelProps {
  date: string; // YYYY-MM-DD
  familyId: number;
  familyTimezone: string | undefined;
  selectedChildId: number | 'all';
  familyChildren: Child[];
  heartDayStatusByChild: Record<number, boolean>; // childId → earned heart
  onClose: () => void;
}

function formatDisplayDate(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

const InsightsDrillDownPanel: React.FC<InsightsDrillDownPanelProps> = ({
  date,
  familyId,
  familyTimezone,
  selectedChildId,
  familyChildren,
  heartDayStatusByChild,
  onClose,
}) => {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [breakdown, setBreakdown] = useState<BehaviorBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const childId = selectedChildId === 'all' ? undefined : selectedChildId;
    const fetchBreakdown = async () => {
      setIsLoading(true);
      setBreakdown(null);
      try {
        const data = await insightsAPI.getBehaviorBreakdownForDay(familyId, date, childId, familyTimezone);
        setBreakdown(data);
      } catch {
        setBreakdown({ stars: 0, triangles: 0, squares: 0 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBreakdown();
  }, [date, familyId, selectedChildId, familyTimezone]);

  const selectedChild = selectedChildId !== 'all'
    ? familyChildren.find(c => (c.id ?? c.childId) === selectedChildId)
    : null;

  const heartDayEarned = selectedChildId !== 'all'
    ? heartDayStatusByChild[selectedChildId as number]
    : null;

  const positivityRatio = breakdown
    ? computePositivityRatio(breakdown.stars, breakdown.triangles, breakdown.squares)
    : '—';

  return (
    <Card sx={{ mb: 3 }} data-testid="drill-down-panel">
      <CardContent sx={isNaluV1Theme ? { p: 3 } : undefined}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant={isNaluV1Theme ? 'h4' : 'h6'}>{formatDisplayDate(date)}</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close drill-down panel">
            <CloseIcon />
          </IconButton>
        </Box>

        {selectedChildId === 'all' ? (
          /* All Family view */
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Hearts earned:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {familyChildren.map(child => {
                const childId = (child.id ?? child.childId) as number;
                const earned = heartDayStatusByChild[childId];
                return (
                  <Typography key={childId} variant="body2">
                    {child.name}{' '}
                    {/* Icon glyph sizing via fontSize, not a Typography text override */}
                    {earned
                      ? <HeartIcon sx={{ fontSize: 14, color: theme.palette.behaviorHeart, verticalAlign: 'middle' }} />
                      : '—'}
                  </Typography>
                );
              })}
            </Box>
            {isLoading ? (
              <CircularProgress size={16} />
            ) : breakdown && (
              <Typography variant="body2" color="text.secondary">
                Total behaviors: {breakdown.stars + breakdown.triangles + breakdown.squares}
              </Typography>
            )}
          </Box>
        ) : (
          /* Single child view */
          <Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2">
                Heart Day:{' '}
                {/* Icon glyph sizing via fontSize, not a Typography text override */}
                {heartDayEarned === true
                  ? <><HeartIcon sx={{ fontSize: 14, color: theme.palette.behaviorHeart, verticalAlign: 'middle' }} /> Yes</>
                  : '—'}
              </Typography>
              <Typography variant="body2">
                Positivity ratio: {positivityRatio}
              </Typography>
            </Box>

            {selectedChild && (
              <Box sx={{ mb: 2 }}>
                <AnalyticsTimeChart
                  child={selectedChild}
                  selectedDate={date}
                  familyId={familyId}
                  familyTimezone={familyTimezone}
                />
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            {isLoading ? (
              <CircularProgress size={16} />
            ) : breakdown && (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2">⭐ {breakdown.stars} stars</Typography>
                <Typography variant="body2">△ {breakdown.triangles} triangles</Typography>
                <Typography variant="body2">□ {breakdown.squares} squares</Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => { window.location.href = '/'; }}
            aria-label="Log a behavior today"
          >
            Log a behavior today →
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InsightsDrillDownPanel;
